export type Country = {
  /** ISO 3166-1 alpha-2 (ex: BR, US). Também usado pra URL da bandeira SVG. */
  code: string;
  name: string;
  /** DDI sem o "+". */
  dialCode: string;
  /** Máscara opcional (## = dígito). */
  mask?: string;
  /** Exemplo de número pra usar como placeholder quando input está vazio. */
  example?: string;
};

/** Lista curada pra contexto odontológico brasileiro — Brasil primeiro
 *  (país padrão), seguido pelos países mais comuns de pacientes/parceiros. */
export const COUNTRIES: Country[] = [
  { code: "BR", name: "Brasil", dialCode: "55", mask: "(##) #####-####", example: "11987654321" },
  { code: "PT", name: "Portugal", dialCode: "351", mask: "### ### ###", example: "912345678" },
  { code: "US", name: "Estados Unidos", dialCode: "1", mask: "(###) ###-####", example: "2015551234" },
  { code: "AR", name: "Argentina", dialCode: "54", mask: "## ####-####", example: "1123456789" },
  { code: "ES", name: "Espanha", dialCode: "34", mask: "### ### ###", example: "612345678" },
  { code: "IT", name: "Itália", dialCode: "39", mask: "### ### ####", example: "3451234567" },
  { code: "FR", name: "França", dialCode: "33", mask: "# ## ## ## ##", example: "612345678" },
  { code: "DE", name: "Alemanha", dialCode: "49", mask: "### ########", example: "15123456789" },
  { code: "GB", name: "Reino Unido", dialCode: "44", mask: "#### ######", example: "7912345678" },
  { code: "JP", name: "Japão", dialCode: "81", mask: "###-####-####", example: "9012345678" },
  { code: "CA", name: "Canadá", dialCode: "1", mask: "(###) ###-####", example: "4165551234" },
  { code: "MX", name: "México", dialCode: "52", mask: "### ### ####", example: "5512345678" },
  { code: "CL", name: "Chile", dialCode: "56", mask: "# #### ####", example: "912345678" },
  { code: "UY", name: "Uruguai", dialCode: "598", mask: "### ### ###", example: "912345678" },
  { code: "PY", name: "Paraguai", dialCode: "595", mask: "### ### ###", example: "981123456" },
  { code: "CO", name: "Colômbia", dialCode: "57", mask: "### ### ####", example: "3001234567" },
  { code: "PE", name: "Peru", dialCode: "51", mask: "### ### ###", example: "912345678" },
  { code: "AU", name: "Austrália", dialCode: "61", mask: "### ### ###", example: "412345678" },
  { code: "CH", name: "Suíça", dialCode: "41", mask: "## ### ## ##", example: "781234567" },
  { code: "NL", name: "Holanda", dialCode: "31", mask: "# ########", example: "612345678" },
  { code: "AT", name: "Áustria", dialCode: "43", mask: "### #######", example: "6641234567" },
  { code: "BE", name: "Bélgica", dialCode: "32", mask: "### ## ## ##", example: "470123456" },
  { code: "IE", name: "Irlanda", dialCode: "353", mask: "## ### ####", example: "851234567" },
  { code: "CN", name: "China", dialCode: "86", example: "13112345678" },
  { code: "KR", name: "Coreia do Sul", dialCode: "82", example: "1012345678" },
  { code: "IL", name: "Israel", dialCode: "972", example: "501234567" },
  { code: "AE", name: "Emirados Árabes", dialCode: "971", example: "501234567" },
];

export const DEFAULT_COUNTRY = COUNTRIES[0];

export function findCountryByCode(code: string): Country | undefined {
  return COUNTRIES.find((c) => c.code === code);
}

/** URL da bandeira SVG via flagcdn — CDN público, gratuito, sem dep de lib.
 *  Funciona igual em Windows, Mac, Linux (emojis de flag quebram no Windows). */
export function flagUrl(code: string): string {
  return `https://flagcdn.com/${code.toLowerCase()}.svg`;
}

/** Placeholder formatado do país — usa o `example` aplicando a máscara pra
 *  mostrar um telefone real (ex: "(11) 98765-4321"). Sem exemplo, cai num
 *  texto genérico. */
export function placeholderFor(c: Country): string {
  if (c.example) {
    return applyMask(c.example, c.mask);
  }
  return "Número de telefone";
}

/** Aplica a máscara nos dígitos. Para no primeiro char DEPOIS que os dígitos
 *  acabaram — isto é, não emite literais trailing sem conteúdo (permite o
 *  usuário apagar literal → dígito → literal sem o browser "prender" o foco
 *  na máscara vazia). */
export function applyMask(digits: string, mask?: string): string {
  if (!mask) return digits;
  let out = "";
  let d = 0;
  for (const ch of mask) {
    if (d >= digits.length) break;
    if (ch === "#") {
      out += digits[d];
      d++;
    } else {
      out += ch;
    }
  }
  return out;
}
